// src/utils/procurementPdfExporter.js
// Purchase Order's PDF export, built on the shared drawing helpers from
// offlineOrderPdfExporter.js (§9.6) rather than re-implementing them. GRN/RTV/Debit Note already
// have their own export functions there (added Phases 8-9) — re-exported here so all four
// procurement PDF exports live under one import path per §9.6, without touching their existing
// call sites in GoodsReceivedNoteDetailsPage/ReturnToVendorDetailsPage/DebitNoteDetailsPage.
import {
  createDoc,
  buildTheme,
  drawBanner,
  drawTwoColCards,
  drawItemsTable,
  drawTotalsAndNotes,
  formatCurrency,
} from './offlineOrderPdfExporter';

const safeNum = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

export const exportPurchaseOrderPDF = (purchaseOrder, themeColors) => {
  if (!purchaseOrder) return;
  const { doc, pageW, pageH, ml, mr, cw } = createDoc();
  const theme = buildTheme(themeColors);

  drawBanner(doc, {
    pageW, ml, mr, theme,
    title: 'PURCHASE ORDER',
    metaLine: `PO #: ${purchaseOrder.purchaseOrderNumber || '—'}`,
    badgeLabel: (purchaseOrder.status || '').toUpperCase(),
    dateText: `Date: ${new Date(purchaseOrder.orderDate || purchaseOrder.createdAt).toLocaleDateString()}`,
  });

  let y = drawTwoColCards(doc, {
    y: 46, ml, cw, theme,
    left: {
      title: 'Supplier',
      lines: [
        purchaseOrder.Supplier?.name || '—',
        `Phone: ${purchaseOrder.Supplier?.phone || '—'}`,
      ],
    },
    right: {
      title: 'Warehouse / Payment',
      lines: [
        purchaseOrder.Warehouse?.name || '—',
        `Payment: ${(purchaseOrder.paymentStatus || 'unpaid').toUpperCase()}`,
      ],
    },
  });

  const tableItems = (purchaseOrder.items || []).map((item, idx) => {
    const sku = item.ProductVariation?.sku || item.Product?.sku;
    const name = `${item.Product?.name || 'Item'}${sku ? ` (${sku})` : ''}`;
    return [
      String(idx + 1),
      name,
      String(parseFloat(item.quantity)),
      String(parseFloat(item.receivedQuantity)),
      formatCurrency(item.unitPrice),
      formatCurrency(item.lineTotal),
    ];
  });

  y = drawItemsTable(doc, {
    startY: y, ml, mr, pageW, pageH, theme,
    head: ['#', 'Item', 'Ordered', 'Received', 'Unit Price', 'Line Total'],
    body: tableItems,
    footerLabel: `Purchase Order — #${purchaseOrder.purchaseOrderNumber}`,
  });

  const totalsData = [{ label: 'Subtotal', value: formatCurrency(purchaseOrder.totalAmount) }];
  if (safeNum(purchaseOrder.extraCharges) > 0) totalsData.push({ label: 'Extra Charges', value: `+ ${formatCurrency(purchaseOrder.extraCharges)}` });
  if (safeNum(purchaseOrder.discountAmount) > 0) totalsData.push({ label: 'Discount', value: `- ${formatCurrency(purchaseOrder.discountAmount)}` });
  totalsData.push({ label: 'Paid', value: formatCurrency(purchaseOrder.paidAmount) });
  totalsData.push({ label: 'Balance Due', value: formatCurrency(purchaseOrder.balanceAmount) });

  drawTotalsAndNotes(doc, {
    y, ml, pageW, mr, cw, theme, totalsData,
    grandLabel: 'Grand Total', grandValue: formatCurrency(purchaseOrder.grandTotal),
    notesLabel: 'Notes', notesText: purchaseOrder.notes,
  });

  doc.save(`Purchase_Order_${purchaseOrder.purchaseOrderNumber || purchaseOrder.id || 'Doc'}.pdf`);
};

export {
  exportGoodsReceivedNotePDF,
  exportReturnToVendorPDF,
  exportDebitNotePDF,
} from './offlineOrderPdfExporter';
